import React from 'react';
import { useTheme } from '@mui/material/styles';
import { CenteredFlexBox } from '../FlexBox';
import CaptionValue from '../CaptionValue/CaptionValue';
import DbeAvatar from '../DbeAvatar';
import { resolveIcon } from '../../Utils/constants/icons';

/**
 * A value shown with the icon its vocabulary declares for it, on a tinted avatar.
 *
 * The icon arrives by name, so a vocabulary can change which one a value uses without any code
 * change; the tint is derived from the value itself, so a new value is coloured without one either.
 *
 * @param {Object} props - Component props.
 * @param {String} props.icon - Icon name, a key of ICON_BY_NAME.
 * @param {String} props.value - The value shown.
 * @param {String} props.label - Caption above the value; a react-intl message key.
 * @param {String} props.colorKey - What the tint is derived from; the value by default.
 * @param {String} props.color - Overrides the tint entirely.
 * @param {Number} props.size - Avatar size in px.
 *
 * @returns {React.JSX.Element}
 */
const IconValue = function IconValue({
  icon = null,
  value = '',
  label = '',
  colorKey = null,
  color = null,
  size = 40,
}) {
  const theme = useTheme();
  const Icon = resolveIcon(icon);
  const tint = colorKey ?? value;

  return (
    <CenteredFlexBox gap="8px">
      <DbeAvatar
        variant="rounded"
        size={size}
        color={
          color ?? theme.custom.stringToColor(tint?.toUpperCase() || 'DEFAULT')
        }
        icon={<Icon sx={{ color: 'inherit', fontSize: '24px' }} />}
        sx={{ borderRadius: '8px' }}
      />
      <CaptionValue
        valueStyle={{ fontWeight: 500 }}
        caption={label}
        value={value}
      />
    </CenteredFlexBox>
  );
};

export default IconValue;
